import type {
  MarginModifierValue,
  PaddingModifierValue,
  SelectedElementDesignationElement,
} from "schemas/generated/fileModifiers";

import React, { useCallback, useMemo, useState } from "react";

import { LengthInputSelector } from "@editor/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import DirectionBottom from "@svg/modifiers/direction/direction-bottom";
import DirectionHorizontal from "@svg/modifiers/direction/direction-horizontal";
import DirectionLeft from "@svg/modifiers/direction/direction-left";
import DirectionRight from "@svg/modifiers/direction/direction-right";
import DirectionTop from "@svg/modifiers/direction/direction-top";
import DirectionVertical from "@svg/modifiers/direction/direction-vertical";
import ExpandModifierOptions from "@svg/modifiers/expand-modifier-options";

import { useDefaultSelectedElementDesignation } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import IconButton from "@replo/design-system/components/button/IconButton";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { parseUnit } from "replo-runtime/shared/utils/units";

import { PADDING_MODIFIER_OPTIONS } from "../constants";

type SpacingMode = "unified" | "individual";
type SpacingType = "margin" | "padding";
type SpacingModifier = MarginModifierValue | PaddingModifierValue;

interface SpacingValue {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

interface SpacingField {
  key: string;
  label: string;
  value: number | undefined;
  onChange: (newValue: number | undefined) => void;
  icon?: React.ReactNode;
}

interface SpacingModifierConfig {
  type: SpacingType;
  unifiedTooltipText: string;
  individualTooltipText: string;
}

interface SpacingModifierProps<SpacingType extends SpacingModifier> {
  onChange: (modifiers: SpacingType[]) => void;
  config: SpacingModifierConfig;
}

/**
 * Common props for all LengthInputSelector instances
 */
const COMMON_LENGTH_INPUT_PROPS = {
  placeholder: "0px",
  metrics: ["px"],
  unitDefaults: { value: "0", unit: "px" },
  menuOptions: PADDING_MODIFIER_OPTIONS.map((option) => ({
    ...option,
    value: option.value.toString(),
  })),
  inputClassName: "w-full",
  endEnhancer: () => <ChevronDown size={12} />,
};

/**
 * Reusable SpacingInput component
 */
interface SpacingInputProps {
  field: SpacingField;
}

const SpacingInput: React.FC<SpacingInputProps> = ({ field }) => {
  const inputValue = field.value;
  const displayValue = inputValue != null ? `${inputValue}px` : "";

  return (
    <LengthInputSelector
      field={field.key}
      value={displayValue}
      onChange={(newValue) => {
        const result = parseUnit(
          newValue,
          { value: 0, unit: "px" },
          "default",
          "px",
        );
        const numericValue =
          typeof result.value === "number"
            ? result.value
            : Number.parseFloat(result.value);
        field.onChange(numericValue);
      }}
      startEnhancer={field.icon}
      {...COMMON_LENGTH_INPUT_PROPS}
    />
  );
};

function extractSpacingFromStyles(
  styleOverrides: SelectedElementDesignationElement["element"]["styleOverrides"],
  type: SpacingType,
): SpacingValue {
  const spacingValue: SpacingValue = {};

  const stylePrefix = type === "margin" ? "margin" : "padding";

  spacingValue.top = styleOverrides?.[`${stylePrefix}Top`] ?? undefined;
  spacingValue.right = styleOverrides?.[`${stylePrefix}Right`] ?? undefined;
  spacingValue.bottom = styleOverrides?.[`${stylePrefix}Bottom`] ?? undefined;
  spacingValue.left = styleOverrides?.[`${stylePrefix}Left`] ?? undefined;

  return spacingValue;
}

/**
 * Determine initial mode based on spacing consistency.
 * Returns "individual" if any spacing values differ (user likely set them individually).
 * Returns "unified" only if all values are consistent (suitable for unified inputs).
 */
function getInitialMode(spacingValue: SpacingValue): SpacingMode {
  const verticalEqual = spacingValue.top === spacingValue.bottom;
  const horizontalEqual = spacingValue.left === spacingValue.right;

  // Only use unified mode if both vertical AND horizontal pairs are equal
  // If any values differ, start in individual mode to preserve user's intent
  return verticalEqual && horizontalEqual ? "unified" : "individual";
}

const SpacingModifier = <SpacingType extends SpacingModifier>({
  onChange,
  config,
}: SpacingModifierProps<SpacingType>) => {
  const defaultDesignation = useDefaultSelectedElementDesignation();

  const extractedValue = useMemo(() => {
    return defaultDesignation
      ? extractSpacingFromStyles(
          defaultDesignation.element.styleOverrides ?? {},
          config.type,
        )
      : {};
  }, [defaultDesignation, config.type]);

  const [currentValue, setCurrentValue] = useOverridableState(
    extractedValue,
    undefined, // NOTE (Kevin, 2025-07-28): We handle onChange in updateSpacingValue
  );

  const [mode, setMode] = useState<SpacingMode>(() =>
    getInitialMode(currentValue),
  );

  const createModifiersValues = useCallback(
    (value: SpacingValue) => {
      const modifiers: SpacingType[] = [];

      if (value.top !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "top",
          value: value.top,
        } as SpacingType);
      }
      if (value.right !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "right",
          value: value.right,
        } as SpacingType);
      }
      if (value.bottom !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "bottom",
          value: value.bottom,
        } as SpacingType);
      }
      if (value.left !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "left",
          value: value.left,
        } as SpacingType);
      }

      return modifiers;
    },
    [config.type],
  );

  const updateSpacingValue = useCallback(
    (updates: Partial<SpacingValue>) => {
      const newValue = { ...currentValue, ...updates };
      setCurrentValue(newValue);
      const modifiers = createModifiersValues(newValue);
      // TODO (Kurt, 2025-01-27): Bundle up the value instead of sending individual modifier operations
      onChange(modifiers);
    },
    [currentValue, setCurrentValue, createModifiersValues, onChange],
  );

  const inputConfigs: Record<SpacingMode, SpacingField[]> = useMemo(
    () => ({
      unified: [
        {
          key: "vertical",
          label: "Vertical",
          icon: <DirectionVertical className="w-4 h-4" />,
          value: currentValue?.top,
          onChange: (newValue) => {
            updateSpacingValue({
              top: newValue,
              bottom: newValue,
            });
          },
        },
        {
          key: "horizontal",
          label: "Horizontal",
          icon: <DirectionHorizontal className="w-4 h-4" />,
          value: currentValue?.left,
          onChange: (newValue) => {
            updateSpacingValue({
              left: newValue,
              right: newValue,
            });
          },
        },
      ],
      individual: [
        {
          key: "top",
          label: "Top",
          icon: <DirectionTop className="w-4 h-4" />,
          value: currentValue?.top,
          onChange: (newValue) => {
            updateSpacingValue({ top: newValue });
          },
        },
        {
          key: "right",
          label: "Right",
          icon: <DirectionRight className="w-4 h-4" />,
          value: currentValue?.right,
          onChange: (newValue) => {
            updateSpacingValue({ right: newValue });
          },
        },
        {
          key: "bottom",
          label: "Bottom",
          icon: <DirectionBottom className="w-4 h-4" />,
          value: currentValue?.bottom,
          onChange: (newValue) => {
            updateSpacingValue({ bottom: newValue });
          },
        },
        {
          key: "left",
          label: "Left",
          icon: <DirectionLeft className="w-4 h-4" />,
          value: currentValue?.left,
          onChange: (newValue) => {
            updateSpacingValue({ left: newValue });
          },
        },
      ],
    }),
    [currentValue, updateSpacingValue],
  );

  const inputs = inputConfigs[mode];

  const handleModeToggle = () => {
    setMode(mode === "unified" ? "individual" : "unified");
  };

  return (
    <>
      {mode === "unified" ? (
        <div className="grid grid-cols-[1fr_1fr_min-content] gap-2 items-center">
          {inputs.map((input) => (
            <SpacingInput key={input.key} field={input} />
          ))}
          <IconButton
            variant="secondary"
            size="sm"
            onClick={handleModeToggle}
            tooltipText={config.individualTooltipText}
            icon={<ExpandModifierOptions className="w-3 h-3" />}
          />
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_1fr_min-content] gap-2 items-center">
          {/* First row */}
          {inputs.slice(0, 2).map((input) => (
            <SpacingInput key={input.key} field={input} />
          ))}
          <IconButton
            variant="secondary"
            size="sm"
            onClick={handleModeToggle}
            tooltipText={config.unifiedTooltipText}
            icon={<ExpandModifierOptions className="w-3 h-3" />}
          />
          {/* Second row */}
          {inputs.slice(2).map((input) => (
            <SpacingInput key={input.key} field={input} />
          ))}
          <div></div> {/* Empty space to align with button column */}
        </div>
      )}
    </>
  );
};

export default SpacingModifier;
